import defaultLogoUrl from '@/assets/images/Logo_NEOSV.png'
import { env } from '@/config/env'
import { STORAGE_KEYS } from '@/config/constants'
import { FileData, User, UserRole } from '@/types'
import { PortalBranding, StoredPortalBranding } from '@/types/branding.types'
import { storage } from '@/utils/storage'
import { isNeoPlatformRole } from '@/utils/role-access'
import { validateHexColor } from '@/utils/validation'
import { adjustHexColor } from '@/utils/color-utils'

const BRANDING_CSS_VARS = [
  '--color-primary',
  '--color-primary-dark',
  '--color-primary-light',
  '--color-secondary',
  '--color-accent',
  '--color-border-focus',
] as const

export const SYSTEM_BRANDING: PortalBranding = {
  primaryColor: '#08b2b3',
  primaryDark: '#068a8c',
  primaryLight: '#39c1c2',
  secondaryColor: '#078aa8',
  accentColor: '#2ccfdb',
  logoUrl: defaultLogoUrl,
  appName: env.appName,
  isCustom: false,
}

export const brandingStorage = {
  get: (): StoredPortalBranding | null => {
    return storage.get<StoredPortalBranding>(STORAGE_KEYS.PORTAL_BRANDING)
  },

  set: (value: StoredPortalBranding): void => {
    storage.set(STORAGE_KEYS.PORTAL_BRANDING, value)
  },

  remove: (): void => {
    storage.remove(STORAGE_KEYS.PORTAL_BRANDING)
  },
}

export function getLogoUrlFromFileData(file?: FileData): string | null {
  if (!file?.data) return null
  return fileDataToDataUrl(file)
}

function fileDataToDataUrl(file: FileData): string | null {
  if (!file.data) return null

  const extension = file.filename?.split('.').pop()?.toLowerCase()
  const mimeByExtension: Record<string, string> = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    gif: 'image/gif',
  }
  const mime = (extension && mimeByExtension[extension]) || 'image/png'

  return `data:${mime};base64,${file.data}`
}

function buildBrandingPalette(
  primaryColor: string,
  secondaryColor: string
): Pick<PortalBranding, 'primaryColor' | 'primaryDark' | 'primaryLight' | 'secondaryColor' | 'accentColor'> {
  return {
    primaryColor,
    primaryDark: adjustHexColor(primaryColor, -32),
    primaryLight: adjustHexColor(primaryColor, 28),
    secondaryColor,
    accentColor: adjustHexColor(secondaryColor, 36),
  }
}

function hasIdentityData(source: {
  primaryColor?: string
  secondaryColor?: string
  logo?: FileData
  companyName?: string
}): boolean {
  return Boolean(
    source.primaryColor ||
      source.secondaryColor ||
      source.logo?.data ||
      source.companyName?.trim()
  )
}

function resolveIdentitySource(
  user: User
): Pick<User, 'primaryColor' | 'secondaryColor' | 'logo' | 'companyName'> | null {
  if (isNeoPlatformRole(user.role)) {
    return null
  }

  if (user.role === UserRole.WHOLESALER) {
    return user
  }

  if (user.role === UserRole.RESELLER) {
    if (user.wholesaler && typeof user.wholesaler === 'object') {
      return user.wholesaler
    }
    return user
  }

  return null
}

export function resolveBrandingFromStored(stored: StoredPortalBranding | null): PortalBranding {
  if (!stored || !hasIdentityData(stored)) {
    return SYSTEM_BRANDING
  }

  const primaryColor =
    stored.primaryColor && validateHexColor(stored.primaryColor)
      ? stored.primaryColor
      : SYSTEM_BRANDING.primaryColor
  const secondaryColor =
    stored.secondaryColor && validateHexColor(stored.secondaryColor)
      ? stored.secondaryColor
      : SYSTEM_BRANDING.secondaryColor
  const logoUrl = stored.logo ? fileDataToDataUrl(stored.logo) : null

  return {
    ...buildBrandingPalette(primaryColor, secondaryColor),
    logoUrl: logoUrl ?? SYSTEM_BRANDING.logoUrl,
    appName: stored.companyName?.trim() || SYSTEM_BRANDING.appName,
    isCustom: true,
  }
}

export function resolveBrandingFromUser(user: User): PortalBranding {
  const source = resolveIdentitySource(user)
  if (!source || !hasIdentityData(source)) {
    return SYSTEM_BRANDING
  }

  const primaryColor =
    source.primaryColor && validateHexColor(source.primaryColor)
      ? source.primaryColor
      : SYSTEM_BRANDING.primaryColor
  const secondaryColor =
    source.secondaryColor && validateHexColor(source.secondaryColor)
      ? source.secondaryColor
      : SYSTEM_BRANDING.secondaryColor
  const logoUrl = source.logo ? fileDataToDataUrl(source.logo) : null

  return {
    ...buildBrandingPalette(primaryColor, secondaryColor),
    logoUrl: logoUrl ?? SYSTEM_BRANDING.logoUrl,
    appName: source.companyName?.trim() || SYSTEM_BRANDING.appName,
    isCustom: true,
  }
}

function toStoredBranding(branding: PortalBranding, user: User): StoredPortalBranding | null {
  const source = resolveIdentitySource(user)
  if (!source || !branding.isCustom) {
    return null
  }

  return {
    primaryColor: source.primaryColor,
    secondaryColor: source.secondaryColor,
    logo: source.logo,
    companyName: source.companyName,
  }
}

export function applyBrandingToDocument(branding: PortalBranding): void {
  const root = document.documentElement

  if (!branding.isCustom) {
    BRANDING_CSS_VARS.forEach(property => root.style.removeProperty(property))
  } else {
    root.style.setProperty('--color-primary', branding.primaryColor)
    root.style.setProperty('--color-primary-dark', branding.primaryDark)
    root.style.setProperty('--color-primary-light', branding.primaryLight)
    root.style.setProperty('--color-secondary', branding.secondaryColor)
    root.style.setProperty('--color-accent', branding.accentColor)
    root.style.setProperty('--color-border-focus', branding.primaryColor)
  }

  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (favicon) {
    favicon.href = branding.logoUrl
  }
}

export function initializeBranding(): PortalBranding {
  const branding = resolveBrandingFromStored(brandingStorage.get())
  applyBrandingToDocument(branding)
  return branding
}

export function applyBrandingFromUser(user: User): PortalBranding {
  const branding = resolveBrandingFromUser(user)
  applyBrandingToDocument(branding)

  const stored = toStoredBranding(branding, user)
  if (stored) {
    brandingStorage.set(stored)
  } else {
    brandingStorage.remove()
  }

  return branding
}
